import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, TableContainer, Table,
  TableHead, TableBody, TableRow, TableCell, Tabs, Tab, Snackbar,
  MenuItem, Grid, Card, IconButton, useMediaQuery, Pagination, TextField,
  InputAdornment, Avatar, Chip, Stack, Alert, AlertTitle, Tooltip,
  LinearProgress, AccordionSummary, AccordionDetails, Paper, Accordion,
  CardContent, CardActions, Divider,
} from '@mui/material';
import { styled, useTheme, alpha, keyframes } from '@mui/material/styles';
import { useQuery } from 'react-query';
import { useCreatorAuth } from '../../../Hooks/creator-use-auth';
import API from '../../../API';
import { Link, useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import routes from '../../../Config/routes';
import {
  Download as DownloadIcon, ViewModule as ViewModuleIcon, ViewList as ViewListIcon,
  TrendingUp as TrendingUpIcon, AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon, Warning as WarningIcon, Search as SearchIcon,
  Payment as PaymentIcon, AccountBalanceWallet as WalletIcon,
  LocalShipping as TransitIcon, Business as CompanyIcon, Person as UserIcon,
  ChatBubbleOutline as ChatIcon, Info as InfoIcon, ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpIcon, ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon, SwapHoriz as SwapHorizIcon,
  CheckCircle as CheckCircleIcon, Link as LinkIcon,
  Bolt as BoltIcon, Schedule as ScheduleIcon,
} from '@mui/icons-material';

import ChatModalCreator from '../../../Components/ChatComponents/creatorchatmodal';
import CreatorBlitzPayHeader from './creatorcomponents/creatorBlitzPayHeader';
import BalanceLedgerModal from '../../../Pages/BlitzPay/blitzpaypagecomponents/BlitzPayBalanceDialog';
import EnhancedPayoutCard from './creatorcomponents/PayoutCard';
import InvoiceCard from './creatorcomponents/InvoiceCard';
import WalletManagementSection from './creatorcomponents/WalletUseManagement';
import StripeOnboardingRequiredModal from '../../../Components/payments/StripeOnboardingRequiredModal';
import { useStripeOnboardingCheck } from '../../../Hooks/useStripeOnboardingCheck';

// ─────────────────────────────────────────────
// Keyframes / Styled
// ─────────────────────────────────────────────
const gentlePulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.85; }
`;

const StatusChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ theme, status }) => {
  const colors = {
    pending:   { bg: theme.palette.warning.main,  color: theme.palette.warning.contrastText },
    approved:  { bg: theme.palette.info.main,     color: theme.palette.info.contrastText },
    completed: { bg: theme.palette.success.main,  color: theme.palette.success.contrastText },
    cancelled: { bg: theme.palette.error.main,    color: theme.palette.error.contrastText },
    paid:      { bg: theme.palette.success.main,  color: theme.palette.success.contrastText },
    funded:    { bg: theme.palette.success.dark,  color: theme.palette.success.contrastText },
    scheduled: { bg: theme.palette.info.light,    color: theme.palette.info.contrastText },
    confirmed: { bg: theme.palette.info.main,     color: theme.palette.info.contrastText },
  };
  const cfg = colors[status] || { bg: theme.palette.grey[400], color: theme.palette.getContrastText(theme.palette.grey[400]) };
  return { backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.75rem', textTransform: 'capitalize', borderRadius: 6 };
});

const EnhancedCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  background: theme.palette.background.paper,
  '&:hover': { transform: 'translateY(-4px)', boxShadow: theme.shadows[12], borderColor: theme.palette.primary.main },
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: theme.palette.primary.contrastText,
  borderRadius: 20,
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.25)}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: `radial-gradient(circle at top right, ${alpha('#fff', 0.1)} 0%, transparent 70%)`, pointerEvents: 'none' },
  '&::after': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '40%', background: `linear-gradient(180deg, ${alpha('#fff', 0.08)} 0%, transparent 100%)`, pointerEvents: 'none' },
  [theme.breakpoints.down('md')]: { padding: theme.spacing(3), borderRadius: 16 },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12, textTransform: 'none', fontWeight: 600, padding: theme.spacing(1.5, 3),
  boxShadow: theme.shadows[2], transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[6] },
}));

const MetricCard = styled(Box)(({ theme, isPrimary }) => ({
  background: isPrimary
    ? `linear-gradient(135deg, ${alpha('#fff', 0.2)} 0%, ${alpha('#fff', 0.15)} 100%)`
    : `linear-gradient(135deg, ${alpha('#fff', 0.12)} 0%, ${alpha('#fff', 0.08)} 100%)`,
  borderRadius: theme.spacing(2), padding: theme.spacing(2.5),
  border: isPrimary ? `2px solid ${alpha('#fff', 0.3)}` : `1px solid ${alpha('#fff', 0.15)}`,
  backdropFilter: 'blur(10px)', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative', overflow: 'hidden',
  '&:hover': {
    background: isPrimary
      ? `linear-gradient(135deg, ${alpha('#fff', 0.25)} 0%, ${alpha('#fff', 0.2)} 100%)`
      : `linear-gradient(135deg, ${alpha('#fff', 0.18)} 0%, ${alpha('#fff', 0.12)} 100%)`,
    transform: 'translateY(-2px)', boxShadow: `0 4px 16px ${alpha('#000', 0.15)}`,
  },
}));

const StyledAccordion = styled(Accordion, { shouldForwardProp: p => p !== 'hasContent' })(({ theme, hasContent }) => ({
  background: `linear-gradient(135deg, ${alpha('#fff', 0.12)} 0%, ${alpha('#fff', 0.08)} 100%)`,
  backdropFilter: 'blur(10px)', borderRadius: theme.spacing(2), border: `1px solid ${alpha('#fff', 0.15)}`,
  boxShadow: 'none', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: hasContent ? `${gentlePulse} 3s ease-in-out infinite` : 'none',
  '&:before': { display: 'none' },
  '&.Mui-expanded': { margin: 0, background: `linear-gradient(135deg, ${alpha('#fff', 0.18)} 0%, ${alpha('#fff', 0.12)} 100%)`, animation: 'none' },
  '& .MuiAccordionSummary-root': { minHeight: 48, borderRadius: theme.spacing(2), '&:hover': { background: alpha('#fff', 0.08) } },
  '& .MuiAccordionDetails-root': { padding: theme.spacing(2) },
}));

const HorizontalAccordion = styled(Box)(({ theme }) => ({ display: 'flex', alignItems: 'stretch', gap: theme.spacing(2), width: '100%' }));
const HorizontalAccordionTrigger = styled(Box, { shouldForwardProp: p => p !== 'expanded' && p !== 'hasContent' })(({ theme, expanded, hasContent }) => ({
  background: hasContent
    ? `linear-gradient(135deg, ${alpha('#fff', 0.18)} 0%, ${alpha('#fff', 0.12)} 100%)`
    : `linear-gradient(135deg, ${alpha('#fff', 0.12)} 0%, ${alpha('#fff', 0.08)} 100%)`,
  backdropFilter: 'blur(10px)', borderRadius: theme.spacing(2),
  border: `1px solid ${alpha('#fff', hasContent ? 0.25 : 0.15)}`,
  padding: theme.spacing(2), cursor: 'pointer', transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', minWidth: 200,
  position: 'relative', overflow: 'hidden',
  animation: hasContent && !expanded ? `${gentlePulse} 3s ease-in-out infinite` : 'none',
  '&:hover': { background: `linear-gradient(135deg, ${alpha('#fff', 0.22)} 0%, ${alpha('#fff', 0.16)} 100%)`, transform: 'translateY(-2px)', boxShadow: `0 4px 16px ${alpha('#000', 0.15)}` },
}));
const HorizontalAccordionContent = styled(Box)(({ theme }) => ({ display: 'flex', gap: theme.spacing(2), flex: 1 }));

// ─────────────────────────────────────────────────────────────────────────────
// Currency formatter — used by BbcPaymentCard and the main component
// ─────────────────────────────────────────────────────────────────────────────
const currency = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' });
const fmt = (n) => currency.format(Number(n || 0));

// ─────────────────────────────────────────────────────────────────────────────
// BBC PAYMENT CARD — updated to match EnhancedPayoutCard header style
// Drop this in place of the existing BbcPaymentCard in PayoutsInvoicing.jsx
// ─────────────────────────────────────────────────────────────────────────────
const BbcPaymentCard = ({ payment, onElectInstant, electingPaymentId }) => {
  const theme      = useTheme();
  const bbc_status = payment.bbc_status || payment.status || 'promised';
  const decision   = payment.decision || null;
  const gross      = parseFloat(payment.gross_amount  || payment.amount || 0);
  const net        = parseFloat(payment.net_credited  || payment.amount || 0);
  const fee        = parseFloat(payment.fee_charged   || 0);
  const feeRate    = parseFloat(payment.instant_fee_rate || 5);
  const netPreview = parseFloat(payment.instant_net_preview || (gross * (1 - feeRate / 100)));
  const feePreview = parseFloat(payment.instant_fee_preview || (gross * feeRate / 100));
  const term       = payment.payment_term || 30;

  const isPaid      = ['instant_claimed', 'settled'].includes(bbc_status);
  const isInstant   = bbc_status === 'instant_claimed';
  const isSettled   = bbc_status === 'settled';
  const isPending   = bbc_status === 'promised';
  const isEligible  = !!payment.instant_eligible;
  const isNetLocked = isPending && decision === 'wait' && isEligible;
  const isElectingThis = electingPaymentId === payment.bbc_payment_id || electingPaymentId === payment.id;

  const payer   = payment.client_name || 'Enterprise';
  const fromSme = payment.notes?.replace('Blitz Split — ', '') || 'your client';
  const po      = payment.po_number;

  const displayDate = (() => {
    for (const raw of [payment.payout_date, payment.created_at]) {
      if (!raw) continue;
      const d = new Date(raw);
      if (!isNaN(d.getTime()))
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
    return null;
  })();

  const settleDate = payment.expected_funding_date
    ? new Date(payment.expected_funding_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null;

  // Border / background tint per state
  const borderColor = isInstant
    ? alpha('#FFD600', 0.55)
    : isSettled
    ? alpha(theme.palette.success.main, 0.4)
    : isNetLocked
    ? alpha('#1565c0', 0.3)
    : isPending && isEligible
    ? alpha(theme.palette.warning.main, 0.4)
    : theme.palette.divider;

  const bgColor = isInstant
    ? alpha('#FFD600', 0.03)
    : isSettled
    ? alpha(theme.palette.success.main, 0.03)
    : 'background.paper';

  const statusLabel = isInstant   ? '⚡ Instant paid'
    : isSettled     ? '✓ Settled'
    : isNetLocked   ? '🔒 Net-term set'
    : isEligible    ? 'Ready to elect'
    : 'Awaiting approval';

  const statusColor = isPaid ? 'success' : isNetLocked ? 'info' : isEligible ? 'warning' : 'default';

  // Matches needsAction badge logic in EnhancedPayoutCard
  const needsAction = isPending && isEligible;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: needsAction ? 2 : '1.5px solid',
        borderColor: needsAction ? 'primary.main' : borderColor,
        bgcolor: bgColor,
        position: 'relative',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: needsAction ? 4 : 2 },
      }}
    >
      {/* Action badge — mirrors EnhancedPayoutCard */}
      {needsAction && (
        <Box sx={{ position: 'absolute', top: 12, right: 16, zIndex: 1 }}>
          <Chip
            label="Action Required"
            color="primary"
            size="small"
            sx={{ fontWeight: 600, fontSize: '0.75rem', height: 24 }}
          />
        </Box>
      )}

      <CardContent sx={{ flexGrow: 1, p: 3 }}>

        {/* ── Header — flat icon + stacked text, same as EnhancedPayoutCard ── */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 1.5,
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
              <BoltIcon sx={{ fontSize: 22, color: '#FFD600', flexShrink: 0 }} />
              <Typography
                variant="h6"
                fontWeight={600}
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {payer}
              </Typography>
            </Box>
            {/* Only show status chip here when NOT showing the action badge */}
            {!needsAction && (
              <Chip
                label={statusLabel}
                color={statusColor}
                size="small"
                sx={{ fontWeight: 500, fontSize: '0.75rem', height: 24, textTransform: 'capitalize', flexShrink: 0 }}
              />
            )}
          </Box>

          {/* Sub-line: "Blitz Split" label + date, indented to align with text */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              ml: 4.5,
              mt: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Blitz Split
            </Typography>
            {displayDate && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary">
                  {displayDate}
                </Typography>
              </Box>
            )}
          </Box>

          {po && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 4.5, mt: 0.5, display: 'block' }}
            >
              PO: {po}
            </Typography>
          )}
        </Box>

        {/* ── Amount block — unchanged ── */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            mb: 2,
            bgcolor: isPaid
              ? isInstant ? alpha('#FFD600', 0.1) : alpha(theme.palette.success.main, 0.07)
              : alpha(theme.palette.primary.main, 0.06),
            border: '1px solid',
            borderColor: isPaid
              ? isInstant ? alpha('#FFD600', 0.3) : alpha(theme.palette.success.main, 0.2)
              : alpha(theme.palette.primary.main, 0.15),
          }}
        >
          {/* PAID — instant */}
          {isInstant && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: fee > 0 ? 1 : 0 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  You received (instant)
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#7a5c00' }}>
                  {fmt(net)}
                </Typography>
              </Box>
              {fee > 0 && (
                <>
                  <Divider sx={{ my: 1, borderColor: alpha('#FFD600', 0.3) }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary">
                      Gross {fmt(gross)} · {feeRate}% BlitzPay fee
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      −{fmt(fee)}
                    </Typography>
                  </Box>
                </>
              )}
            </>
          )}

          {/* PAID — settled at net term */}
          {isSettled && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                You received (net-{term})
              </Typography>
              <Typography variant="h5" fontWeight={700} color="success.main">
                {fmt(gross)}
              </Typography>
            </Box>
          )}

          {/* PENDING — approved, can elect */}
          {isPending && isEligible && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>Invoice total</Typography>
                <Typography variant="h5" fontWeight={700}>{fmt(gross)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" sx={{ color: '#7a5c00' }}>
                  ⚡ Instant ({feeRate}% fee)
                </Typography>
                <Typography variant="body2" fontWeight={700} sx={{ color: '#7a5c00' }}>
                  {fmt(netPreview)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="success.main" fontWeight={500}>
                  Net-{term} (no fee)
                </Typography>
                <Typography variant="body2" fontWeight={700} color="success.main">
                  {fmt(gross)}
                </Typography>
              </Box>
            </>
          )}

          {/* PENDING — awaiting enterprise approval */}
          {isPending && !isEligible && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>Incoming</Typography>
                <Typography variant="h5" fontWeight={700}>{fmt(gross)}</Typography>
              </Box>
              {settleDate && (
                <Typography variant="caption" color="text.disabled" display="block" mt={0.75}>
                  Est. settlement {settleDate} · Net-{term}
                </Typography>
              )}
            </>
          )}
        </Box>

        {/* ── Source note ── */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.66rem', lineHeight: 1.4, display: 'block' }}
        >
          Via Blitz Split from {fromSme}
        </Typography>

      </CardContent>

      {/* ── Footer action / status ── */}
      <Box sx={{ px: 3, pb: 2.5 }}>
        {isInstant && (
          <Chip
            label="⚡ Paid instantly via BlitzPay"
            color="success"
            icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
            sx={{
              width: '100%', fontWeight: 600, fontSize: '0.8rem', height: 34,
              bgcolor: alpha('#FFD600', 0.15), color: '#7a5c00',
              border: `1px solid ${alpha('#FFD600', 0.4)}`,
              '& .MuiChip-icon': { color: '#7a5c00' },
            }}
          />
        )}

        {isSettled && (
          <Chip
            label={`✓ Settled in full — no fee · net-${term}`}
            color="success"
            icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
            sx={{ width: '100%', fontWeight: 600, fontSize: '0.8rem', height: 34 }}
          />
        )}

        {/* NET-LOCKED — SME set net-term, creator can override */}
        {isNetLocked && (
          <Box sx={{
            borderRadius: 2, border: '1px solid', borderColor: alpha('#1565c0', 0.3),
            bgcolor: alpha('#1565c0', 0.04), overflow: 'hidden',
          }}>
            <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: alpha('#1565c0', 0.15) }}>
              <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 700, fontSize: '0.68rem', display: 'block', mb: 0.25 }}>
                🔒 Your client set this to net-{term}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1.4 }}>
                You receive {fmt(gross)} in full at net-{term} — no fee.
                {settleDate && ` Est. ${settleDate}.`}
              </Typography>
            </Box>
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1.4, display: 'block', mb: 1 }}>
                Want it now instead? You can override to instant — you'd receive {fmt(netPreview)} after the {feeRate}% fee.
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                size="small"
                disabled={isElectingThis}
                onClick={() => onElectInstant?.(payment.bbc_payment_id || payment.id)}
                startIcon={isElectingThis
                  ? <CircularProgress size={10} />
                  : <BoltIcon sx={{ fontSize: '13px !important', color: '#7a5c00' }} />}
                sx={{
                  height: 30, fontSize: '0.7rem', fontWeight: 700,
                  borderColor: alpha('#FFD600', 0.5), color: '#7a5c00',
                  bgcolor: alpha('#FFD600', 0.08),
                  textTransform: 'none', borderRadius: 1.5,
                  '&:hover': { bgcolor: alpha('#FFD600', 0.18), borderColor: '#FFD600' },
                }}
              >
                {isElectingThis ? 'Processing…' : `Get ${fmt(netPreview)} now (instant)`}
              </Button>
            </Box>
          </Box>
        )}

        {/* PENDING & ELIGIBLE — not net-locked yet, creator elects here */}
        {isPending && isEligible && !isNetLocked && (
          <Box sx={{
            borderRadius: 2, border: `1px solid ${alpha('#FFD600', 0.35)}`,
            bgcolor: alpha('#FFD600', 0.06), overflow: 'hidden',
          }}>
            <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#7a5c00', fontWeight: 700, fontSize: '0.68rem', display: 'block', mb: 0.25 }}>
                You're in the money — choose your timing
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', lineHeight: 1.4 }}>
                Instant: {fmt(netPreview)} now ({feeRate}% fee) · Net-{term}: {fmt(gross)} full amount, no fee.
              </Typography>
            </Box>
            <Box sx={{ px: 1.5, pb: 1.25, pt: 0.75 }}>
              <Button
                fullWidth
                variant="contained"
                size="small"
                disabled={isElectingThis}
                onClick={() => onElectInstant?.(payment.bbc_payment_id || payment.id)}
                startIcon={isElectingThis
                  ? <CircularProgress size={10} sx={{ color: '#1A1200' }} />
                  : <BoltIcon sx={{ fontSize: '13px !important' }} />}
                sx={{
                  height: 30, fontSize: '0.7rem', fontWeight: 700,
                  bgcolor: '#FFD600', color: '#1A1200',
                  textTransform: 'none', borderRadius: 1.5, boxShadow: 'none',
                  '&:hover': { bgcolor: '#ECC900', boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: alpha('#FFD600', 0.3) },
                }}
              >
                {isElectingThis ? 'Processing…' : `Get ${fmt(netPreview)} instantly`}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block', textAlign: 'center', mt: 0.5, lineHeight: 1.3 }}>
                Or do nothing — you automatically receive {fmt(gross)} at net-{term}
              </Typography>
            </Box>
          </Box>
        )}

        {isPending && !isEligible && (
          <Box sx={{
            py: 0.75, textAlign: 'center',
            bgcolor: alpha(theme.palette.grey[500], 0.06),
            borderRadius: 2, border: `1px solid ${alpha(theme.palette.grey[500], 0.15)}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
              <ScheduleIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.68rem' }}>
                Unlocks once enterprise approves this invoice
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Card>
  );
};

const STATUS_PRIORITY = { approved: 5, confirmed: 4, completed: 3, paid: 3, funded: 3, pending: 2, scheduled: 1, cancelled: 0 };

const pickBestStatus = (a, b) => {
  const pa = STATUS_PRIORITY[(a || '').toLowerCase()] ?? -1;
  const pb = STATUS_PRIORITY[(b || '').toLowerCase()] ?? -1;
  return pa >= pb ? a : b;
};

const mergeCreatorPayoutsAndInvoices = (rawPayouts, rawInvoices) => {
  const usedInvoiceIds = new Set();
  const usedPayoutIds  = new Set();
  const mergedPayouts  = [];

  const invoiceByPo = {};
  for (const inv of rawInvoices) {
    const po = inv.po_number || inv.unique_code;
    if (po && !invoiceByPo[po]) invoiceByPo[po] = inv;
  }

  for (const payout of rawPayouts) {
    const po = payout.po_number || payout.unique_code;
    if (!po) continue;
    const matchedInvoice = invoiceByPo[po];
    if (!matchedInvoice || usedInvoiceIds.has(matchedInvoice.id)) continue;
    usedInvoiceIds.add(matchedInvoice.id);
    usedPayoutIds.add(payout.id);
    const mergedStatus = pickBestStatus(payout.status, matchedInvoice.status);
    mergedPayouts.push({
      ...matchedInvoice, ...payout,
      id: payout.id, recordType: 'payout', isMerged: true,
      linkedInvoiceId: matchedInvoice.id, invoiceData: matchedInvoice,
      status: mergedStatus,
      client_name: matchedInvoice.client_name || payout.client_name,
      amount: payout.amount || matchedInvoice.amount || matchedInvoice.amount_due,
      payout_date: payout.payout_date || matchedInvoice.payout_date,
      created_at:  payout.created_at  || matchedInvoice.created_at,
      notes: payout.notes && matchedInvoice.notes && payout.notes !== matchedInvoice.notes
        ? `${payout.notes} | ${matchedInvoice.notes}`
        : payout.notes || matchedInvoice.notes,
    });
  }

  const unmatchedPayouts  = rawPayouts.filter(p => !usedPayoutIds.has(p.id));
  const unmatchedInvoices = rawInvoices.filter(i => !usedInvoiceIds.has(i.id));
  const remainingInvoices = [...unmatchedInvoices];

  for (const payout of unmatchedPayouts) {
    const payoutAmt  = Number(payout.amount || 0);
    const payoutDate = new Date(payout.payout_date || payout.created_at);
    if (payoutAmt === 0) continue;
    const idx = remainingInvoices.findIndex(inv => {
      const invAmt  = Number(inv.amount || inv.amount_due || 0);
      const invDate = new Date(inv.payout_date || inv.created_at);
      if (invAmt === 0) return false;
      const amtClose  = Math.abs(invAmt - payoutAmt) / payoutAmt <= 0.01;
      const daysApart = Math.abs((payoutDate - invDate) / 86_400_000);
      return amtClose && daysApart <= 14;
    });
    if (idx !== -1) {
      const inv = remainingInvoices.splice(idx, 1)[0];
      usedInvoiceIds.add(inv.id);
      usedPayoutIds.add(payout.id);
      mergedPayouts.push({
        ...inv, ...payout,
        id: payout.id, recordType: 'payout', isMerged: true,
        linkedInvoiceId: inv.id, invoiceData: inv,
        status: pickBestStatus(payout.status, inv.status),
        client_name: inv.client_name || payout.client_name,
        amount: payout.amount || inv.amount || inv.amount_due,
        payout_date: payout.payout_date || inv.payout_date,
        created_at:  payout.created_at  || inv.created_at,
        notes: payout.notes || inv.notes,
      });
    }
  }

  return {
    mergedPayouts,
    unmatchedPayouts:  rawPayouts.filter(p => !usedPayoutIds.has(p.id)),
    unmatchedInvoices: rawInvoices.filter(i => !usedInvoiceIds.has(i.id)),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const PayoutsInvoicing = () => {
  const { creatorToken } = useCreatorAuth();
  const username = creatorToken?.creator_user?.username;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [paymentDialogOpen, setPaymentDialogOpen]     = useState(false);
  const [paymentMethod, setPaymentMethod]             = useState({ payoutId: null, method: '', destination: 'withdraw' });
  const [stripeVerifyDialog, setStripeVerifyDialog]   = useState({ open: false, url: null });
  const [onboardingModalOpen, setOnboardingModalOpen] = useState(false);
  const [onboardingBlock, setOnboardingBlock]         = useState(null);
  const [claimingPayoutId, setClaimingPayoutId]       = useState(null);
  const [activeTab, setActiveTab]                     = useState('all');
  const [statusFilter, setStatusFilter]               = useState('all');
  const [searchTerm, setSearchTerm]                   = useState('');
  const [page, setPage]                               = useState(1);
  const [snackbarOpen, setSnackbarOpen]               = useState(false);
  const [snackbarMessage, setSnackbarMessage]         = useState('');
  const [viewMode, setViewMode]                       = useState('grid');
  const [showWalletManagement, setShowWalletManagement] = useState(false);
  const [chatOpen, setChatOpen]                       = useState(false);
  const [otherBalancesExpanded, setOtherBalancesExpanded] = useState(false);
  const [balanceLedgerOpen, setBalanceLedgerOpen]     = useState(false);

  // ── BBC instant-elect state (creator overrides SME net-term) ────
  const [bbcElecting, setBbcElecting] = useState(null); // payment id being elected

  // ── Queries ──────────────────────────────────────────────────────────────
  const { isLoading, error, data: financials, refetch: refetchFinancials } = useQuery(
    ['creator', 'financials'],
    () => API.creatorConnect.getFinancials(username),
    { keepPreviousData: true, enabled: !!username }
  );

  const { error: trackingError, data: trackingData = [], refetch: refetchTracking } = useQuery(
    ['creator', 'paymentTracking'],
    () => API.creatorConnect.getPaymentTracking(),
    { keepPreviousData: true, enabled: !!username }
  );

  const { isLoading: balanceLoading, error: balanceError, data: balanceData, refetch: refetchBalance } = useQuery(
    ['creator', 'balance', 'me'],
    () => API.balanceLedger.fetchMyBalance(),
    { keepPreviousData: true, refetchInterval: 30000, enabled: !!username }
  );

  const { isLoading: walletsLoading, data: walletsData, refetch: refetchWallets } = useQuery(
    ['creator', 'wallets'],
    () => API.creatorConnect.getWallets(),
    { keepPreviousData: true, enabled: !!username }
  );

  const { data: taxInfoData } = useQuery(
    ['creator', 'taxInfo'],
    () => API.creatorConnect.getTaxInfo(),
    { keepPreviousData: true, enabled: !!username }
  );

  const { data: ledgerHistory } = useQuery(
    ['creator', 'balanceLedger', balanceData?.user?.id],
    () => API.balanceLedger.fetch(balanceData?.user?.id),
    { enabled: !!balanceData?.user?.id, keepPreviousData: true }
  );

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleOpenPaymentDialog = useCallback((payoutId) => {
    setPaymentDialogOpen(true);
    setPaymentMethod({ payoutId, method: 'BLITZPAY', destination: 'withdraw' });
  }, []);

  const handleClosePaymentDialog = useCallback(() => {
    setPaymentDialogOpen(false);
    setPaymentMethod({ payoutId: null, method: '', destination: 'withdraw' });
  }, []);

  const handleClaimToBalance = useCallback(async (payoutId) => {
    try {
      setClaimingPayoutId(payoutId);
      await API.creatorConnect.claimPayout(payoutId, 'balance');
      setSnackbarMessage('Payout successfully claimed to your balance!');
      setSnackbarOpen(true);
      refetchFinancials(); refetchTracking(); refetchBalance();
    } catch (err) {
      setSnackbarMessage(err?.response?.data?.error || 'Failed to claim payout');
      setSnackbarOpen(true);
    } finally { setClaimingPayoutId(null); }
  }, [refetchFinancials, refetchTracking, refetchBalance]);

  const handleClaimNet30 = useCallback(async (payoutId) => {
    try {
      setClaimingPayoutId(payoutId);
      await API.creatorConnect.claimPayout(payoutId, 'balance', 'NET30');
      setSnackbarMessage("Payment claimed! You'll receive the full amount in 30 days with no fees.");
      setSnackbarOpen(true);
      refetchFinancials(); refetchTracking();
    } catch (err) {
      setSnackbarMessage(err?.response?.data?.error || 'Failed to claim payout');
      setSnackbarOpen(true);
    } finally { setClaimingPayoutId(null); }
  }, [refetchFinancials, refetchTracking]);

  const wallets = walletsData?.wallets || [];
  const primaryWallet = wallets.find(w => w.is_primary);
  const primaryWalletIsAch = ['bank_ach', 'stripe_ach_payout'].includes(primaryWallet?.wallet_type);

  const {
    needsOnboarding: achNeedsOnboarding,
    missingFields: achMissingFields,
    onboardingUrl: achOnboardingUrl,
    refetch: refetchOnboardingStatus,
  } = useStripeOnboardingCheck({
    enabled: paymentDialogOpen && primaryWalletIsAch,
    mode: 'authenticated',
    returnUrl: typeof window !== 'undefined' ? window.location.href : undefined,
  });

  const handleConfirmPayment = useCallback(async () => {
    if (primaryWalletIsAch && achNeedsOnboarding) {
      setOnboardingBlock({ missingFields: achMissingFields, onboardingUrl: achOnboardingUrl });
      setOnboardingModalOpen(true);
      return;
    }
    setClaimingPayoutId(paymentMethod.payoutId);
    try {
      const response = await API.creatorConnect.claimPayout(paymentMethod.payoutId, 'withdraw', paymentMethod.method);
      setSnackbarMessage(response?.message || 'Payout confirmed and withdrawal initiated!');
      setSnackbarOpen(true);
      handleClosePaymentDialog();
      refetchFinancials(); refetchTracking(); refetchBalance();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 422 && data?.error_code === 'stripe_onboarding_incomplete') {
        setOnboardingBlock({ missingFields: data.missing_fields || [], onboardingUrl: data.onboarding_url });
        setOnboardingModalOpen(true);
        if (refetchOnboardingStatus) refetchOnboardingStatus();
        return;
      }
      const code = data?.code;
      const onboardingUrl = data?.onboarding_url;
      if (code === 'CONNECT_CAPABILITY_INACTIVE' && onboardingUrl) {
        setStripeVerifyDialog({ open: true, url: onboardingUrl });
      } else if (code === 'NO_PAYMENT_METHOD') {
        setSnackbarMessage('Please add a payment method to withdraw funds');
      } else {
        setSnackbarMessage(data?.error || 'Failed to process withdrawal');
      }
      setSnackbarOpen(true);
    } finally { setClaimingPayoutId(null); }
  }, [paymentMethod, refetchFinancials, refetchTracking, refetchBalance, handleClosePaymentDialog, primaryWalletIsAch, achNeedsOnboarding, achMissingFields, achOnboardingUrl, refetchOnboardingStatus]);

  const handleInstantWithdrawal = useCallback(async (payoutId) => {
    try {
      setClaimingPayoutId(payoutId);
      const response = await API.creatorConnect.claimPayout(payoutId, 'withdraw', 'BLITZPAY');
      setSnackbarMessage(response?.message || 'Instant payout processed! Funds are on the way.');
      setSnackbarOpen(true);
      refetchFinancials(); refetchTracking(); refetchBalance();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 422 && data?.error_code === 'stripe_onboarding_incomplete') {
        setOnboardingBlock({ missingFields: data.missing_fields || [], onboardingUrl: data.onboarding_url });
        setOnboardingModalOpen(true);
        return;
      }
      const code = data?.code;
      const onboardingUrl = data?.onboarding_url;
      if (code === 'CONNECT_CAPABILITY_INACTIVE' && onboardingUrl) {
        setStripeVerifyDialog({ open: true, url: onboardingUrl });
      } else if (code === 'NO_PAYMENT_METHOD') {
        setSnackbarMessage('Please add a payment method to withdraw funds');
      } else {
        setSnackbarMessage(data?.error || 'Failed to process withdrawal');
      }
      setSnackbarOpen(true);
    } finally { setClaimingPayoutId(null); }
  }, [refetchFinancials, refetchTracking, refetchBalance]);

  const handleApproveCampaignPayout = useCallback(async (payoutId, paymentMethodChoice) => {
    if (!username) { setSnackbarMessage('Not signed in'); setSnackbarOpen(true); return; }
    try {
      setClaimingPayoutId(payoutId);
      await API.creatorConnect.confirmPayment({ username, paymentMethod: paymentMethodChoice, payout_id: payoutId });
      setSnackbarMessage('Payout timing saved. Waiting for admin to confirm.');
      setSnackbarOpen(true);
      refetchFinancials(); refetchTracking();
    } catch (err) {
      setSnackbarMessage(err?.response?.data?.error || 'Failed to save payout timing');
      setSnackbarOpen(true);
    } finally { setClaimingPayoutId(null); }
  }, [username, refetchFinancials, refetchTracking]);

  const handleTabChange    = useCallback((_, v) => { setActiveTab(v); setPage(1); }, []);

  // ── BBC instant elect (creator overrides SME net-term lock) ─────
  const handleBbcElectInstant = useCallback(async (paymentId) => {
    try {
      setBbcElecting(paymentId);
      await API.bbc.decide(paymentId, 'instant');
      setSnackbarMessage('⚡ Instant payout elected! Funds are being credited to your balance.');
      setSnackbarOpen(true);
      refetchFinancials();
    } catch (err) {
      setSnackbarMessage(err?.response?.data?.error || 'Could not elect instant — please try again');
      setSnackbarOpen(true);
    } finally {
      setBbcElecting(null);
    }
  }, [refetchFinancials]);
  const handleStatusChange = useCallback((e) => { setStatusFilter(e.target.value); setPage(1); }, []);
  const handleCloseSnackbar = useCallback((_, r) => r !== 'clickaway' && setSnackbarOpen(false), []);

  const handleSetPrimaryWallet = useCallback(async (walletId) => {
    try {
      await API.creatorConnect.setPrimaryWallet(walletId);
      await refetchWallets();
      setSnackbarMessage('Primary payment method updated successfully!'); setSnackbarOpen(true);
    } catch { setSnackbarMessage('Failed to update primary payment method'); setSnackbarOpen(true); }
  }, [refetchWallets]);

  const navigateToSettings = useCallback((tab = 'wallets') => navigate(`/creatorconnect/edit?tab=${tab}`), [navigate]);
  const openCEOChat = useCallback(() => setChatOpen(true), []);

  const getAmount = useCallback((item) => Number(item?.amount ?? item?.amount_due ?? 0), []);

  const getFinalizedAmount = useCallback((item) => {
    const raw = Number(item?.amount ?? item?.amount_due ?? 0);
    const rate = Number(item?.interest_rate ?? 0);
    if ((item?.role || '').toLowerCase() === 'creator' && rate > 0) return raw * (1 - rate / 100);
    return raw;
  }, []);

  const getItemDate = useCallback((item) => {
    for (const raw of [item.payout_date, item.created_at, item.date, item.timestamp]) {
      if (!raw) continue;
      const d = new Date(raw);
      if (!isNaN(d.getTime())) return d;
    }
    return new Date(0);
  }, []);

  const formatItemDate = useCallback((item) => {
    const d = getItemDate(item);
    if (d.getTime() === 0) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }, [getItemDate]);

  const sortByDate = useCallback((a, b) => getItemDate(b) - getItemDate(a), [getItemDate]);

  const isClaimablePayout = useCallback((item) => {
    const status = (item.status || '').toLowerCase();
    return item.recordType === 'payout'
      && !item.withdrawal_id
      && ((status === 'approved' && !item.campaign_id) || (status === 'pending' && !!item.campaign_id));
  }, []);

  const sortByActionRequired = useCallback((a, b) => {
    const aClaimable = isClaimablePayout(a);
    const bClaimable = isClaimablePayout(b);
    if (aClaimable && !bClaimable) return -1;
    if (!aClaimable && bClaimable) return 1;
    const aPI = a.recordType === 'invoice' && (a.status || '').toLowerCase() === 'pending';
    const bPI = b.recordType === 'invoice' && (b.status || '').toLowerCase() === 'pending';
    if (aPI && !bPI) return -1;
    if (!aPI && bPI) return 1;
    return getItemDate(b) - getItemDate(a);
  }, [getItemDate, isClaimablePayout]);

  const getDisplayStatus    = useCallback((s) => { const n=(s||'').trim().toLowerCase(); if(n==='completed'||n==='confirmed') return 'Paid'; if(n==='funded') return 'Funded'; return s?s.charAt(0).toUpperCase()+s.slice(1).toLowerCase():'Unknown'; }, []);
  const getDisplayStatusKey = useCallback((s) => { const n=(s||'').trim().toLowerCase(); if(n==='completed'||n==='confirmed') return 'paid'; if(n==='funded') return 'funded'; return n; }, []);
  const getProgressValue    = useCallback((s) => ({ approved:33, confirmed:66, completed:100, paid:100 }[(s||'').toLowerCase()] ?? 0), []);
  const getProgressLabel    = useCallback((s, imm) => { const m={approved:'Approved • Awaiting Confirmation', confirmed:'Confirmed • Funds in Transit', completed:'Completed', paid:'Completed'}; const l=m[(s||'').toLowerCase()]||'Processing'; return imm?`${l} (Funds Imminent)`:l; }, []);

  const getSourceInfo = useCallback((item) => {
    let source = { type:'Unknown', name:'N/A', icon:null };
    const from = (e) => { if(!e) return; if(e.company) source={type:'Company',name:e.company.name||'Unnamed Company',icon:<CompanyIcon/>}; else if(e.user) source={type:'User',name:e.user.username||'Anonymous',icon:<UserIcon/>}; else if(e.creator) source={type:'Creator',name:e.creator.display_name||e.creator.creator||'Creator',icon:<UserIcon/>}; };
    if(item.transaction) from(item.transaction);
    else if(item.withdrawal) from(item.withdrawal);
    else if(item.invoice) source={type:'Client',name:item.invoice.client_name||'Unnamed Client',icon:<CompanyIcon/>};
    else if(item.client_name) source={type:'Client',name:item.client_name,icon:<CompanyIcon/>};
    return source;
  }, []);

  const handleDownloadCSV = useCallback(() => {
    const rows = [
      ['ID','Type','Amount','Status','Payout Date','BlitzPay','Fee','Code','Notes','Merged'],
      ...(financials?.invoices||[]).map(i=>[i.id,'Invoice',getAmount(i).toFixed(2),i.status||'',i.payout_date||'',i.blitzpay?'Yes':'No',(((i.interest_rate||0)/100)*getAmount(i)).toFixed(2),i.unique_code||'',(i.notes||'').replace(/\n|\r/g,' '),'No']),
      ...(financials?.payouts||[]).map(p=>[p.id,'Payout',getAmount(p).toFixed(2),p.status||'',p.payout_date||'',p.blitzpay?'Yes':'No',(((p.interest_rate||0)/100)*getAmount(p)).toFixed(2),p.unique_code||'',(p.notes||'').replace(/\n|\r/g,' '),p.isMerged?'Yes':'No']),
      ...(financials?.bbc_payments||[]).map(p=>[p.bbc_payment_id,'Blitz Split',parseFloat(p.amount||0).toFixed(2),p.bbc_status||'',p.payout_date||'',p.blitzpay?'Yes':'No',parseFloat(p.fee_charged||0).toFixed(2),p.po_number||'',(p.notes||'').replace(/\n|\r/g,' '),'No']),
    ];
    const csv = rows.map(r=>r.map(c=>{const s=String(c??'');return s.includes(',')?`"${s.replace(/"/g,'""')}"`:`${s}`;}).join(',')).join('\n');
    saveAs(new Blob([csv],{type:'text/csv;charset=utf-8;'}),`Financial_${Date.now()}.csv`);
  }, [financials, getAmount]);

  // ── Loading / Error ───────────────────────────────────────────────────────
  if (isLoading && !financials) return (
    <Box sx={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', gap:2 }}>
      <CircularProgress size={50} />
      <Typography variant="body1" color="text.secondary">Loading your financial data...</Typography>
    </Box>
  );
  if (error) return (
    <Box sx={{ p:3, textAlign:'center' }}>
      <Alert severity="error" sx={{ maxWidth:500, mx:'auto', borderRadius:3 }}>
        <Typography variant="h6" gutterBottom>Unable to Load Financial Data</Typography>
        <Typography variant="body2">{error.message}</Typography>
      </Alert>
    </Box>
  );
  if (trackingError) return (
    <Box sx={{ p:3, textAlign:'center' }}>
      <Alert severity="error" sx={{ maxWidth:500, mx:'auto', borderRadius:3 }}>
        <Typography variant="h6" gutterBottom>Unable to Load Tracking Data</Typography>
        <Typography variant="body2">{trackingError.message}</Typography>
      </Alert>
    </Box>
  );

  // ─────────────────────────────────────────────────────────────────────────
  // DATA PROCESSING
  // ─────────────────────────────────────────────────────────────────────────
  const rawPayouts  = financials?.payouts      || [];
  const rawInvoices = financials?.invoices     || [];
  // BBC payments come from the same financials endpoint — no extra query needed
  const rawBbcPayments = (financials?.bbc_payments || []).map(p => ({
    ...p,
    recordType: 'bbc_payment',
  }));

  const trackingById = {};
  for (const tp of (trackingData || [])) {
    if (tp.id && tp.campaign_id) trackingById[tp.id] = tp.campaign_id;
  }
  const enrichedPayouts = rawPayouts.map(p =>
    !p.campaign_id && trackingById[p.id] ? { ...p, campaign_id: trackingById[p.id] } : p
  );

  const { mergedPayouts, unmatchedPayouts, unmatchedInvoices } =
    mergeCreatorPayoutsAndInvoices(enrichedPayouts, rawInvoices);

  // BBC payments are shown in payouts tab alongside regular payouts
  const payoutsWithType = [
    ...mergedPayouts.map(p  => ({ ...p, recordType: 'payout' })),
    ...unmatchedPayouts.map(p => ({ ...p, recordType: 'payout' })),
    ...rawBbcPayments,
  ].sort(sortByActionRequired);

  const invoicesWithType = unmatchedInvoices
    .map(i => ({ ...i, recordType: 'invoice' }))
    .sort(sortByActionRequired);

  const enrichedLedgerEntries = (ledgerHistory || [])
    .filter(e => (e.transaction_type || e.operation_type || '') === 'internal_transfer')
    .map(e => {
      const changeAmt = parseFloat(e.change_amount || e.amount || 0);
      const amount    = Math.abs(changeAmt);
      const direction = e.direction || (changeAmt > 0 ? 'credit' : 'debit');
      return { ...e, recordType:'transfer', amount, date:e.created_at||e.timestamp, payout_date:e.created_at||e.timestamp, created_at:e.created_at||e.timestamp, status:e.status||'completed', po_number:`TXN-${e.id}`, direction, notes:e.brief||e.description||'Internal transfer' };
    });

  const allData = [
    ...payoutsWithType,
    ...invoicesWithType,
    ...enrichedLedgerEntries,
  ].sort(sortByDate);

  const taxInfo            = taxInfoData?.tax_info;
  const totalInTransit     = (trackingData || []).reduce((s, p) => s + getAmount(p), 0) || 0;
  const unverifiedFunds    = invoicesWithType
    .filter(i => (i.status || '').trim().toLowerCase() === 'pending')
    .reduce((s, i) => s + getAmount(i), 0);
  const blitzBankBalance   = balanceData?.balance ? parseFloat(balanceData.balance) : 0;
  const hasVerifiedWallets = wallets.some(w => w.is_primary === true || w.verification_status === 'verified');

  const applyFilters = (arr) => {
    let f = arr;
    if (statusFilter !== 'all') f = f.filter(i => i.status && i.status.trim().toLowerCase() === statusFilter);
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      f = f.filter(i =>
        (i.notes || '').toLowerCase().includes(s) ||
        (i.unique_code || i.po_number || '').toLowerCase().includes(s) ||
        (i.client_name || '').toLowerCase().includes(s) ||
        String(i.id || '').includes(searchTerm)
      );
    }
    return f;
  };

  const getDisplayedData = () => {
    let base;
    switch (activeTab) {
      case 'payouts':  base = payoutsWithType; break;
      case 'invoices': base = invoicesWithType; break;
      case 'transit':  base = (trackingData || []).map(p => ({ ...p, recordType:'payout' })); break;
      default:         base = allData;
    }
    const filtered = applyFilters(base);
    if (activeTab === 'all' && statusFilter === 'all') return filtered.filter(i => (i.status||'').trim().toLowerCase() !== 'cancelled');
    return filtered;
  };

  const displayedData = getDisplayedData();
  const itemsPerPage  = 6;
  const totalPages    = Math.ceil(displayedData.length / itemsPerPage) || 1;
  const pagedData     = displayedData.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const hasSecondaryBalances = unverifiedFunds > 0 || totalInTransit > 0;

  // ── Card renderer ─────────────────────────────────────────────────────────
  const renderItemCard = (item) => {
    const isInvoice  = item.recordType === 'invoice';
    const isTransfer = item.recordType === 'transfer';
    const isBbc      = item.recordType === 'bbc_payment';

    if (isTransfer) {
      const isCredit    = item.direction === 'credit';
      const displayDate = formatItemDate(item);
      return (
        <EnhancedCard sx={{ height:'100%', display:'flex', flexDirection:'column' }}>
          <CardContent sx={{ flexGrow:1, pb:1 }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', mb:2 }}>
              <Box sx={{ display:'flex', alignItems:'center', gap:1, flex:1 }}>
                <SwapHorizIcon color="info" sx={{ fontSize:20 }} />
                <Typography variant="subtitle1" fontWeight={600}>Transfer</Typography>
              </Box>
              <StatusChip status={item.status} label={getDisplayStatus(item.status)} size="small" />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Transaction ID: {item.po_number}</Typography>
            {displayDate && <Typography variant="caption" color="text.secondary" display="block" mb={2}>{displayDate}</Typography>}
            <Typography variant="h5" fontWeight="bold" color={isCredit?'success.main':'error.main'} sx={{ mb:1.5 }}>
              {isCredit?'+':'-'}{currency.format(item.amount)}
            </Typography>
            <Chip label={isCredit?'Credit':'Debit'} size="small" color={isCredit?'success':'error'} sx={{ mb:1 }} />
            {item.notes && <Typography variant="body2" color="text.secondary" sx={{ mt:1, fontSize:'0.875rem' }}>{item.notes}</Typography>}
          </CardContent>
        </EnhancedCard>
      );
    }

    if (isInvoice) {
      return (
        <InvoiceCard
          invoice={item}
          getDisplayStatus={getDisplayStatus}
          getDisplayStatusKey={getDisplayStatusKey}
          getFinalizedAmount={getFinalizedAmount}
          theme={theme}
        />
      );
    }

    // ── BBC payment — rendered with dedicated card ──
    if (isBbc) {
      return <BbcPaymentCard payment={item} onElectInstant={handleBbcElectInstant} electingPaymentId={bbcElecting} />;
    }

    // Regular payout (including merged payout+invoice records)
    return (
      <EnhancedPayoutCard
        payout={item}
        onClaimToBalance={handleClaimToBalance}
        onClaimWithWithdrawal={handleInstantWithdrawal}
        onClaimNet30={handleClaimNet30}
        onApproveCampaignPayout={handleApproveCampaignPayout}
        wallets={wallets}
        claimingPayoutId={claimingPayoutId}
        formatItemDate={formatItemDate}
      />
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p:{ xs:0, md:3 }, bgcolor:{ xs:'transparent', md:'grey.50' }, minHeight:'100vh' }}>

      {/* ── Header ── */}
      {isMobile ? (
        <Box sx={{ px:2.5, pt:2.5, pb:1 }} data-tour="earnings-overview">
          <CreatorBlitzPayHeader
            currentBalance={blitzBankBalance}
            balanceLoading={balanceLoading}
            onShowBalanceLedger={() => setBalanceLedgerOpen(true)}
            onOpenCEOChat={openCEOChat}
            walletCount={wallets.length}
            hasVerifiedWallets={hasVerifiedWallets}
          />
          {hasSecondaryBalances && (
            <Box sx={{ mt:2 }}>
              <StyledAccordion hasContent={hasSecondaryBalances} sx={{ background:'transparent', border:'none' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color:'text.primary' }} />} sx={{ bgcolor:alpha(theme.palette.primary.main,0.05), borderRadius:3, border:`1px solid ${alpha(theme.palette.primary.main,0.1)}` }}>
                  <Typography variant="body2" fontWeight={600}>View Other Balances</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt:2 }}>
                  <Stack spacing={2}>
                    {unverifiedFunds > 0 && (
                      <Paper elevation={0} sx={{ p:2, bgcolor:alpha(theme.palette.warning.main,0.05), borderRadius:2, border:`1px solid ${alpha(theme.palette.warning.main,0.2)}` }}>
                        <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}><WarningIcon sx={{ fontSize:'1rem', color:'warning.main' }} /><Typography variant="body2" fontWeight={600}>Unverified Funds</Typography><Tooltip title="Pending invoices awaiting approval." arrow><HelpIcon sx={{ fontSize:'0.9rem', color:'text.secondary', cursor:'help' }} /></Tooltip></Box>
                        <Typography variant="h5" fontWeight="bold">{currency.format(unverifiedFunds)}</Typography>
                        <Typography variant="caption" color="text.secondary">Pending approval</Typography>
                      </Paper>
                    )}
                    {totalInTransit > 0 && (
                      <Paper elevation={0} sx={{ p:2, bgcolor:alpha(theme.palette.info.main,0.05), borderRadius:2, border:`1px solid ${alpha(theme.palette.info.main,0.2)}` }}>
                        <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}><TransitIcon sx={{ fontSize:'1rem', color:'info.main' }} /><Typography variant="body2" fontWeight={600}>In Transit</Typography><Tooltip title="Funds being processed." arrow><HelpIcon sx={{ fontSize:'0.9rem', color:'text.secondary', cursor:'help' }} /></Tooltip></Box>
                        <Typography variant="h5" fontWeight="bold">{currency.format(totalInTransit)}</Typography>
                        <Typography variant="caption" color="text.secondary">{trackingData.length} payout{trackingData.length!==1?'s':''} en route</Typography>
                      </Paper>
                    )}
                  </Stack>
                </AccordionDetails>
              </StyledAccordion>
            </Box>
          )}
        </Box>
      ) : (
        <HeaderCard elevation={0} data-tour="earnings-overview">
          <Grid container spacing={4} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:3 }}>
                <Avatar sx={{ bgcolor:alpha('#fff',0.25), width:{ xs:48, md:56 }, height:{ xs:48, md:56 }, boxShadow:`0 4px 12px ${alpha('#000',0.15)}` }}>
                  <WalletIcon sx={{ color:'white', fontSize:{ xs:'1.5rem', md:'2rem' } }} />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ fontSize:{ xs:'1.5rem', md:'2rem' } }}>Blitz T2</Typography>
                  <Typography variant="body2" sx={{ opacity:0.9, fontSize:{ xs:'0.8rem', md:'0.875rem' } }}>The Future of Finance, at your fingertips</Typography>
                </Box>
              </Box>
              <HorizontalAccordion>
                <MetricCard isPrimary sx={{ flex:'0 0 auto', minWidth:280, cursor:'pointer', '&:hover':{ transform:'translateY(-4px)', boxShadow:`0 6px 20px ${alpha('#000',0.2)}` } }} onClick={() => setBalanceLedgerOpen(true)}>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
                    <WalletIcon sx={{ fontSize:'1.2rem' }} />
                    <Typography variant="body2" fontWeight={600} sx={{ opacity:0.9 }}>Blitz Balance</Typography>
                    <Tooltip title="Funds held in USDC. Click to view transaction history." arrow placement="top">
                      <InfoIcon sx={{ fontSize:'1rem', opacity:0.7, cursor:'help' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    {balanceLoading ? <CircularProgress size={24} sx={{ color:alpha('#fff',0.7) }} /> : currency.format(blitzBankBalance)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity:0.8 }}>Click to view history</Typography>
                </MetricCard>
                {hasSecondaryBalances && (
                  <>
                    {!otherBalancesExpanded ? (
                      <HorizontalAccordionTrigger onClick={() => setOtherBalancesExpanded(true)} hasContent={hasSecondaryBalances} sx={{ flex:'0 0 auto' }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ opacity:0.9, mb:0.5 }}>Other Balances</Typography>
                          <Typography variant="caption" sx={{ opacity:0.7 }}>{unverifiedFunds>0&&totalInTransit>0?'2 accounts':'1 account'}</Typography>
                        </Box>
                        <ChevronRightIcon sx={{ opacity:0.7 }} />
                      </HorizontalAccordionTrigger>
                    ) : (
                      <HorizontalAccordionContent>
                        {unverifiedFunds > 0 && (
                          <MetricCard sx={{ flex:1 }}>
                            <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}><WarningIcon sx={{ fontSize:'1.2rem' }} /><Typography variant="body2" fontWeight={600} sx={{ opacity:0.9 }}>Unverified Funds</Typography></Box>
                            <Typography variant="h5" fontWeight="bold">{currency.format(unverifiedFunds)}</Typography>
                            <Typography variant="caption" sx={{ opacity:0.8 }}>Pending approval</Typography>
                          </MetricCard>
                        )}
                        {totalInTransit > 0 && (
                          <MetricCard sx={{ flex:1 }}>
                            <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}><TransitIcon sx={{ fontSize:'1.2rem' }} /><Typography variant="body2" fontWeight={600} sx={{ opacity:0.9 }}>In Transit</Typography></Box>
                            <Typography variant="h5" fontWeight="bold">{currency.format(totalInTransit)}</Typography>
                            <Typography variant="caption" sx={{ opacity:0.8 }}>{trackingData.length} payout{trackingData.length!==1?'s':''} en route</Typography>
                          </MetricCard>
                        )}
                        <HorizontalAccordionTrigger onClick={() => setOtherBalancesExpanded(false)} sx={{ flex:'0 0 auto', minWidth:'auto', px:2 }}>
                          <ChevronLeftIcon sx={{ opacity:0.7 }} />
                        </HorizontalAccordionTrigger>
                      </HorizontalAccordionContent>
                    )}
                  </>
                )}
              </HorizontalAccordion>
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack spacing={2}>
                <ActionButton variant="contained" component={Link} to={routes.creatorConnectInternalTransfer} fullWidth data-tour="request-payout-btn" sx={{ bgcolor:alpha('#fff',0.25), color:'white', '&:hover':{ bgcolor:alpha('#fff',0.35), boxShadow:`0 6px 20px ${alpha('#000',0.2)}` }, py:{ xs:1.75, md:2 }, fontSize:{ xs:'0.9rem', md:'1rem' }, boxShadow:`0 4px 12px ${alpha('#000',0.15)}` }} startIcon={<SwapHorizIcon />}>Transfer Balance</ActionButton>
                <ActionButton variant="outlined" component={Link} to="/creatorinvoicing" fullWidth data-tour="create-invoice-btn" sx={{ borderColor:alpha('#fff',0.6), color:'white', borderWidth:2, '&:hover':{ borderColor:'white', bgcolor:alpha('#fff',0.15), borderWidth:2, boxShadow:`0 4px 12px ${alpha('#000',0.15)}` }, fontSize:{ xs:'0.85rem', md:'0.95rem' } }} startIcon={<ReceiptIcon />}>Request Money</ActionButton>
                <ActionButton variant="outlined" fullWidth onClick={openCEOChat} sx={{ borderColor:alpha('#fff',0.6), color:'white', borderWidth:2, '&:hover':{ borderColor:'white', bgcolor:alpha('#fff',0.15), borderWidth:2, boxShadow:`0 4px 12px ${alpha('#000',0.15)}` }, fontSize:{ xs:'0.85rem', md:'0.95rem' } }} startIcon={<ChatIcon />}>Questions? Chat with our CEO</ActionButton>
                <Box sx={{ textAlign:'center', pt:1 }} data-tour="payment-settings">
                  <Typography variant="caption" sx={{ opacity:0.85, fontSize:{ xs:'0.7rem', md:'0.75rem' } }}>
                    {wallets.length>0 ? <>{wallets.length} payment method{wallets.length!==1?'s':''} configured{!hasVerifiedWallets&&' • Verification needed'}</> : 'No payment methods configured'}
                  </Typography>
                  <Button variant="text" size="small" onClick={() => navigateToSettings('wallets')} sx={{ color:alpha('#fff',0.95), textTransform:'none', fontSize:{ xs:'0.7rem', md:'0.75rem' }, '&:hover':{ color:'white', bgcolor:alpha('#fff',0.1) }, mt:0.5 }}>
                    {wallets.length>0 ? 'Manage Payment Methods' : 'Setup BlitzPay in settings'}
                  </Button>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </HeaderCard>
      )}

      {balanceError && <Alert severity="warning" sx={{ mb:3, mx:{ xs:2.5, md:0 }, borderRadius:3 }}><Typography variant="body2">Could not load Blitz balance: {balanceError.message}</Typography></Alert>}

      {!hasVerifiedWallets && (invoicesWithType.length > 0 || payoutsWithType.length > 0) && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb:2, mx:{ xs:2.5, md:0 }, borderRadius:3 }} action={<Button color="inherit" size="small" onClick={() => navigateToSettings('wallets')}>Add Payment Method</Button>}>
          <AlertTitle>Payment Method Required</AlertTitle>
          You have pending invoices/payouts. Add a verified payment method to receive automatic payments.
        </Alert>
      )}

      {showWalletManagement && (
        <Box sx={{ mx:{ xs:2.5, md:0 } }} data-tour="payment-settings">
          <WalletManagementSection wallets={wallets} walletsLoading={walletsLoading} taxInfo={taxInfo} hasVerifiedWallets={hasVerifiedWallets} onSetPrimaryWallet={handleSetPrimaryWallet} onNavigateToSettings={navigateToSettings} />
        </Box>
      )}

      {totalInTransit > 0 && activeTab === 'transit' && (
        <EnhancedCard sx={{ mb:3, mx:{ xs:2.5, md:0 }, p:{ xs:2, md:3 } }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:2 }}>
            <TransitIcon color="primary" sx={{ fontSize:{ xs:28, md:32 } }} />
            <Box sx={{ flex:1 }}>
              <Typography variant="h6" fontWeight={600} sx={{ fontSize:{ xs:'1rem', md:'1.25rem' } }}>Tracking {trackingData.length} Payment{trackingData.length!==1?'s':''}</Typography>
              <Typography variant="body2" color="text.secondary">Total value: {currency.format(totalInTransit)}</Typography>
            </Box>
          </Box>
          <LinearProgress variant="determinate" value={trackingData.length?trackingData.reduce((s,p)=>s+getProgressValue(p.status),0)/trackingData.length:0} sx={{ height:8, borderRadius:4, mb:1, bgcolor:alpha(theme.palette.primary.main,0.1), '& .MuiLinearProgress-bar':{ borderRadius:4, background:`linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)` } }} />
          <Typography variant="body2" color="text.secondary">Overall progress across all tracked payments</Typography>
        </EnhancedCard>
      )}

      {/* ── Filters ── */}
      <EnhancedCard sx={{ p:{ xs:2, md:3 }, mb:3, mx:{ xs:2.5, md:0 } }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField fullWidth size="small" placeholder="Search by notes, client, code, or ID…" value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} InputProps={{ startAdornment:<InputAdornment position="start"><SearchIcon sx={{ color:'text.secondary' }} /></InputAdornment> }} sx={{ '& .MuiOutlinedInput-root':{ borderRadius:3, transition:'all 0.2s ease', '&:hover':{ boxShadow:`0 2px 8px ${alpha(theme.palette.primary.main,0.1)}` }, '&.Mui-focused':{ boxShadow:`0 2px 12px ${alpha(theme.palette.primary.main,0.2)}` } } }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField select fullWidth size="small" label="Status" value={statusFilter} onChange={handleStatusChange} sx={{ '& .MuiOutlinedInput-root':{ borderRadius:3 } }}>
              {['all','pending','approved','confirmed','completed','cancelled','paid','scheduled'].map(s=>(
                <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <ActionButton variant="outlined" fullWidth size="small" startIcon={<DownloadIcon />} onClick={handleDownloadCSV}>Export</ActionButton>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <Box sx={{ display:'flex', gap:1, justifyContent:'flex-end' }}>
              <Tooltip title="Grid view"><IconButton color={viewMode==='grid'?'primary':'default'} onClick={()=>setViewMode('grid')} sx={{ border:1, borderColor:viewMode==='grid'?'primary.main':'divider', borderRadius:2, transition:'all 0.2s ease', '&:hover':{ borderColor:'primary.main', transform:'scale(1.05)' } }}><ViewModuleIcon /></IconButton></Tooltip>
              <Tooltip title="List view"><IconButton color={viewMode==='list'?'primary':'default'} onClick={()=>setViewMode('list')} sx={{ border:1, borderColor:viewMode==='list'?'primary.main':'divider', borderRadius:2, transition:'all 0.2s ease', '&:hover':{ borderColor:'primary.main', transform:'scale(1.05)' } }}><ViewListIcon /></IconButton></Tooltip>
            </Box>
          </Grid>
        </Grid>
      </EnhancedCard>

      {/* ── Tabs ── */}
      <EnhancedCard sx={{ mb:3, mx:{ xs:2.5, md:0 } }}>
        <Tabs value={activeTab} onChange={handleTabChange} variant={isMobile?'scrollable':'fullWidth'} scrollButtons={isMobile?'auto':false}
          sx={{ '& .MuiTab-root':{ textTransform:'none', fontWeight:600, fontSize:{ xs:'0.85rem', md:'0.95rem' }, py:{ xs:2, md:2.5 }, transition:'all 0.2s ease', '&:hover':{ bgcolor:alpha(theme.palette.primary.main,0.05) } }, '& .MuiTabs-indicator':{ height:3, borderRadius:'3px 3px 0 0' } }}>
          <Tab label={<Box sx={{ display:'flex', alignItems:'center', gap:1 }}><TrendingUpIcon fontSize="small" /><span style={{ display:isMobile?'none':'inline' }}>All Activity</span><span style={{ display:isMobile?'inline':'none' }}>All</span></Box>} value="all" />
          <Tab label={<Box sx={{ display:'flex', alignItems:'center', gap:1 }}><AccountBalanceIcon fontSize="small" /> Payouts{rawBbcPayments.length>0&&<Chip label={`+${rawBbcPayments.length} Split`} size="small" color="default" sx={{ height:18, fontSize:'0.6rem', ml:0.5, bgcolor:alpha('#FFD600',0.2), color:'#7a5c00', border:`1px solid ${alpha('#FFD600',0.4)}` }} />}</Box>} value="payouts" />
          <Tab label={<Box sx={{ display:'flex', alignItems:'center', gap:1 }}><ReceiptIcon fontSize="small" /> Invoices{invoicesWithType.length>0&&<Chip label={invoicesWithType.length} size="small" color="default" sx={{ height:18, fontSize:'0.65rem', ml:0.5 }} />}</Box>} value="invoices" />
          <Tab label={<Box sx={{ display:'flex', alignItems:'center', gap:1 }}><TransitIcon fontSize="small" /><span style={{ display:isMobile?'none':'inline' }}>In Transit</span><span style={{ display:isMobile?'inline':'none' }}>Transit</span>{totalInTransit>0&&<Chip label={trackingData.length} size="small" color="primary" sx={{ height:20, fontSize:'0.7rem', ml:0.5 }} />}</Box>} value="transit" />
        </Tabs>
      </EnhancedCard>

      {/* ── Data ── */}
      <Box sx={{ mx:{ xs:2.5, md:0 } }} data-tour="transaction-list">
        {displayedData.length === 0 ? (
          <EnhancedCard sx={{ p:{ xs:4, md:6 }, textAlign:'center' }}>
            <AccountBalanceIcon sx={{ fontSize:{ xs:48, md:64 }, color:'text.secondary', mb:2, opacity:0.5 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>No records found</Typography>
            <Typography variant="body2" color="text.secondary">Try adjusting your filters or search terms</Typography>
          </EnhancedCard>
        ) : viewMode === 'list' ? (
          <EnhancedCard>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight:600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight:600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight:600 }}>Amount</TableCell>
                    {!isMobile && <TableCell sx={{ fontWeight:600 }}>Date</TableCell>}
                    {!isMobile && <TableCell sx={{ fontWeight:600 }}>Progress</TableCell>}
                    {!isMobile && <TableCell sx={{ fontWeight:600 }}>Source</TableCell>}
                    <TableCell sx={{ fontWeight:600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedData.map((item) => {
                    const isInvoice  = item.recordType === 'invoice';
                    const isTransfer = item.recordType === 'transfer';
                    const isBbc      = item.recordType === 'bbc_payment';
                    const isMerged   = item.isMerged === true;
                    const statusKey  = getDisplayStatusKey(item.status);
                    const imminent   = !!item.imminent;
                    const sourceInfo = getSourceInfo(item);
                    const displayDate = formatItemDate(item) || '—';
                    const isCredit   = item.direction === 'credit';
                    const needsAction = !isTransfer && !isInvoice && !isBbc && isClaimablePayout(item);
                    const isManualQueue = item.withdrawal?.processing_mode === 'manual_fallback';

                    // BBC row display values
                    const bbcStatus = isBbc ? (item.bbc_status || item.status) : null;
                    const bbcPaid   = isBbc && ['instant_claimed','settled'].includes(bbcStatus);
                    const bbcNet    = isBbc ? parseFloat(item.net_credited || item.amount || 0) : 0;
                    const bbcGross  = isBbc ? parseFloat(item.gross_amount || item.amount || 0) : 0;

                    return (
                      <TableRow key={`${item.recordType}-${item.id}`} sx={{
                        '&:hover':{ bgcolor:alpha(theme.palette.primary.main,0.04) },
                        transition:'background-color 0.2s ease',
                        bgcolor: isBbc
                          ? alpha('#FFD600', 0.02)
                          : needsAction ? alpha(theme.palette.primary.main,0.02) : 'inherit',
                      }}>
                        {/* Type cell */}
                        <TableCell>
                          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                            {isTransfer
                              ? <SwapHorizIcon fontSize="small" color="info" />
                              : isInvoice
                              ? <ReceiptIcon fontSize="small" color="action" />
                              : isBbc
                              ? <BoltIcon fontSize="small" sx={{ color:'#7a5c00' }} />
                              : <PaymentIcon fontSize="small" color="primary" />}
                            <Box>
                              <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                                <Typography variant="body2" fontWeight={600}>
                                  {isTransfer ? 'Transfer' : isInvoice ? 'Invoice' : isBbc ? 'Blitz Split' : 'Payout'}
                                </Typography>
                                {isMerged && !isBbc && <Tooltip title="Invoice + Payout combined (same PO)"><LinkIcon sx={{ fontSize:14, color:'text.disabled' }} /></Tooltip>}
                              </Box>
                              {needsAction && <Typography variant="caption" color="primary.main" fontWeight={700} sx={{ display:'block' }}>⚡ Action Required</Typography>}
                              {isBbc && item.client_name && (
                                <Typography variant="caption" color="text.secondary" sx={{ display:'block', fontSize:'0.65rem' }} noWrap>
                                  {item.client_name}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Status cell */}
                        <TableCell>
                          <Box sx={{ display:'flex', gap:0.5, alignItems:'center', flexWrap:'wrap' }}>
                            {isBbc ? (
                              <Chip
                                label={bbcStatus === 'instant_claimed' ? '⚡ Instant paid' : bbcStatus === 'settled' ? '✓ Settled' : item.instant_eligible ? 'Ready to elect' : 'Awaiting approval'}
                                size="small"
                                color={bbcPaid ? 'success' : item.instant_eligible ? 'warning' : 'default'}
                                sx={{ fontWeight:600, fontSize:'0.68rem', height:20,
                                  ...(bbcStatus === 'instant_claimed' ? { bgcolor:alpha('#FFD600',0.2), color:'#7a5c00', border:`1px solid ${alpha('#FFD600',0.4)}` } : {}) }}
                              />
                            ) : (
                              <>
                                <StatusChip status={statusKey} label={getDisplayStatus(item.status)} size="small" />
                                {item.pending_payment_method && <Tooltip title="Add payment method to process"><Chip label="Payment Method Needed" size="small" color="warning" /></Tooltip>}
                                {item.withdrawal_id && <Tooltip title={`Withdrawal ID: ${item.withdrawal_id}`}><Chip label="Withdrawal Created" size="small" color="success" icon={<CheckCircleIcon />} /></Tooltip>}
                                {isManualQueue && <Chip label="Queued for Manual Processing" size="small" color="warning" />}
                                {isTransfer && <Chip label={isCredit?'Credit':'Debit'} size="small" color={isCredit?'success':'error'} variant="outlined" />}
                              </>
                            )}
                          </Box>
                        </TableCell>

                        {/* Amount cell */}
                        <TableCell>
                          {isBbc ? (
                            <Box>
                              <Typography variant="body1" fontWeight={700} color={bbcPaid ? 'success.main' : 'text.primary'}>
                                {currency.format(bbcPaid ? bbcNet : bbcGross)}
                              </Typography>
                              {bbcStatus === 'instant_claimed' && bbcNet !== bbcGross && (
                                <Typography variant="caption" color="text.disabled" sx={{ fontSize:'0.62rem' }}>
                                  gross {currency.format(bbcGross)}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body1" fontWeight={600} color={isTransfer?(isCredit?'success.main':'error.main'):'text.primary'}>
                              {isTransfer&&(isCredit?'+':'-')}{currency.format(getFinalizedAmount(item))}
                            </Typography>
                          )}
                        </TableCell>

                        {!isMobile && <TableCell><Typography variant="body2">{displayDate}</Typography></TableCell>}

                        {!isMobile && (
                          <TableCell sx={{ minWidth:150 }}>
                            {!isTransfer && !isBbc && (
                              <>
                                <LinearProgress variant="determinate" value={getProgressValue(item.status)} sx={{ height:6, borderRadius:3, mb:0.5, bgcolor:alpha(theme.palette.primary.main,0.1), '& .MuiLinearProgress-bar':{ borderRadius:3, background:`linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)` } }} />
                                <Typography variant="caption" color="text.secondary">{getProgressLabel(item.status,imminent)}</Typography>
                              </>
                            )}
                            {isBbc && item.po_number && (
                              <Typography variant="caption" color="text.disabled">PO {item.po_number}</Typography>
                            )}
                          </TableCell>
                        )}

                        {!isMobile && (
                          <TableCell>
                            {!isTransfer && (
                              <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                                {isBbc ? <BoltIcon sx={{ fontSize:16, color:'#7a5c00' }} /> : sourceInfo.icon}
                                <Typography variant="body2" noWrap sx={{ maxWidth:150 }}>
                                  {isBbc ? (item.notes?.replace('Blitz Split — ','') || item.client_name || 'Blitz Split') : (item.client_name || sourceInfo.name)}
                                </Typography>
                              </Box>
                            )}
                          </TableCell>
                        )}

                        {/* Actions cell */}
                        <TableCell>
                          {isBbc ? (
                            <Chip
                              label={
                                bbcStatus === 'instant_claimed' ? '⚡ Instant'
                                : bbcStatus === 'settled' ? '✓ Settled'
                                : item.instant_eligible ? 'Elect on Payments page'
                                : 'Pending approval'
                              }
                              size="small"
                              color={bbcPaid ? 'success' : 'default'}
                              sx={{ fontSize:'0.68rem', fontWeight:600 }}
                            />
                          ) : isInvoice ? (
                            <Button size="small" variant="outlined" component={Link} to={`/invoicing/${item.id}`} sx={{ borderRadius:2 }}>View</Button>
                          ) : isTransfer ? (
                            <Chip label="Completed" size="small" color="default" />
                          ) : isClaimablePayout(item) ? (
                            <Box sx={{ display:'flex', gap:1 }}>
                              {!item.paypal_fee_covered && <Tooltip title="Add to your Blitz balance"><Button size="small" variant="outlined" onClick={()=>handleClaimToBalance(item.id)} disabled={claimingPayoutId===item.id} sx={{ borderRadius:2, minWidth:'auto', px:1.5 }}>{claimingPayoutId===item.id?'...':'Balance'}</Button></Tooltip>}
                              <Tooltip title={wallets.length>0?'Withdraw to your payment method':'Add a payment method first'}><span><Button size="small" variant="contained" color="success" onClick={()=>handleOpenPaymentDialog(item.id)} disabled={wallets.length===0||claimingPayoutId===item.id} sx={{ borderRadius:2, minWidth:'auto', px:1.5 }}>Withdraw</Button></span></Tooltip>
                            </Box>
                          ) : (
                            <Chip label={item.blitzpay?'BlitzPay':'Net30'} size="small" variant="outlined" color={item.blitzpay?'primary':'default'} />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </EnhancedCard>
        ) : (
          <Grid container spacing={3}>
            {pagedData.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={`${item.recordType}-${item.id}`}>
                {renderItemCard(item)}
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display:'flex', justifyContent:'center', mt:4, mx:{ xs:2.5, md:0 } }}>
          <Pagination count={totalPages} page={page} onChange={(_,v)=>setPage(v)} color="primary" size={isMobile?'small':'medium'} showFirstButton showLastButton sx={{ '& .MuiPaginationItem-root':{ borderRadius:2, fontWeight:600 } }} />
        </Box>
      )}

      {/* ── Payment Dialog ── */}
      <Dialog open={paymentDialogOpen} onClose={handleClosePaymentDialog} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:3, boxShadow:`0 8px 32px ${alpha('#000',0.15)}` } }}>
        <DialogTitle><Box sx={{ display:'flex', alignItems:'center', gap:1 }}><PaymentIcon color="primary" /><Typography variant="h6" fontWeight={600}>Withdraw to Payment Method</Typography></Box></DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb:3 }}>
            Select your withdrawal speed. Funds will be sent to your primary payment method
            {wallets.length>0&&wallets.find(w=>w.is_primary)&&<strong> ({wallets.find(w=>w.is_primary)?.nickname||wallets.find(w=>w.is_primary)?.wallet_type?.replace('_',' ')})</strong>}.
          </DialogContentText>
          <TextField select fullWidth label="Payment Method" value={paymentMethod.method} onChange={e=>setPaymentMethod(prev=>({ ...prev, method:e.target.value }))} sx={{ '& .MuiOutlinedInput-root':{ borderRadius:2 } }}>
            <MenuItem value="BLITZPAY"><Box sx={{ display:'flex', alignItems:'center', gap:2 }}><TrendingUpIcon color="primary" /><Box><Typography variant="body1" fontWeight={600}>BlitzPay</Typography><Typography variant="caption" color="text.secondary">Instant payment</Typography></Box></Box></MenuItem>
            <MenuItem value="NET30"><Box sx={{ display:'flex', alignItems:'center', gap:2 }}><AccountBalanceIcon color="action" /><Box><Typography variant="body1" fontWeight={600}>Net 30</Typography><Typography variant="caption" color="text.secondary">Standard payment terms</Typography></Box></Box></MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px:3, pb:3 }}>
          <Button onClick={handleClosePaymentDialog} variant="outlined" sx={{ borderRadius:2 }}>Cancel</Button>
          <Button onClick={handleConfirmPayment} variant="contained" disabled={!paymentMethod.method || claimingPayoutId === paymentMethod.payoutId} sx={{ borderRadius:2 }}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {/* ── Stripe Identity Verification Dialog ── */}
      <Dialog open={stripeVerifyDialog.open} onClose={() => setStripeVerifyDialog({ open: false, url: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, boxShadow: 6 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Verify Your Identity to Receive Payouts</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To receive payouts, Stripe requires a one-time identity verification.
            This takes about 2 minutes and only needs to be done once.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setStripeVerifyDialog({ open: false, url: null })} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={() => { window.open(stripeVerifyDialog.url, '_blank'); setStripeVerifyDialog({ open: false, url: null }); }}>Complete Verification</Button>
        </DialogActions>
      </Dialog>

      <StripeOnboardingRequiredModal
        open={onboardingModalOpen}
        onClose={() => setOnboardingModalOpen(false)}
        missingFields={onboardingBlock?.missingFields || achMissingFields}
        onboardingUrl={onboardingBlock?.onboardingUrl || achOnboardingUrl}
        context="claim"
      />
      <BalanceLedgerModal open={balanceLedgerOpen} onClose={()=>setBalanceLedgerOpen(false)} />
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar} message={snackbarMessage} />
      <ChatModalCreator open={chatOpen} onClose={()=>setChatOpen(false)} recipientUserId={1} />
    </Box>
  );
};

export default PayoutsInvoicing;